import { call, put, takeLatest, select } from 'redux-saga/effects';
import { getTask, postTask } from '../../services';
import { Urls } from '../../config/urls';
import {
  getCurrenciesSuccess,
  getCurrenciesError,
  postCreatePaymentLinkSuccess,
  postCreatePaymentLinkError,
  getDocumentTypesForPaymentLinkSuccess,
  getDocumentTypesForPaymentLinkError,
  getExistingClientsSuccess,
  getExistingClientsError,
  getCreatedPaymentLinksSuccess,
  getCreatedPaymentLinksError,
  PaymentLinkActionType,
} from './actions';
import { checkToken } from '../auth/sagas';
import { CheckoutTypes } from '../../constants';
import { AuthSelectors } from '../auth/selectors';
import { PanelStore } from '../interfaces';
import { segmentPaymentLinkCreatePaymentLink } from '../../utils/analytics';

function* getCurrencies(action: any) {
  yield* checkToken();
  const token: string = yield select(AuthSelectors.getToken);

  const { response, error } = yield call(getTask, {
    url: `${Urls.api.currencies}?country=${action.country}`,
    token,
  });

  if (response) {
    yield put(getCurrenciesSuccess(response));
  } else {
    yield put(getCurrenciesError(error));
  }
}

export function* getCurrenciesSaga() {
  yield takeLatest(PaymentLinkActionType.GET_CURRENCIES, getCurrencies);
}

function* getDocumentTypesForPaymentLink(action: any) {
  yield* checkToken();
  const token: string = yield select(AuthSelectors.getToken);

  const { response, error } = yield call(getTask, {
    url: `${Urls.api.documentTypes}?country=${action.country}`,
    token,
  });

  if (response) {
    yield put(getDocumentTypesForPaymentLinkSuccess(response));
  } else {
    yield put(getDocumentTypesForPaymentLinkError(error));
  }
}

export function* getDocumentTypesForPaymentLinkSaga() {
  yield takeLatest(PaymentLinkActionType.GET_DOCUMENT_TYPES_FOR_PAYMENT_LINK, getDocumentTypesForPaymentLink);
}

function* createPaymentLink(action: any) {
  yield* checkToken();
  const token: string = yield select(AuthSelectors.getToken);
  const state: PanelStore.RootState = yield select();

  const body = { subType: CheckoutTypes.LINK, ...action.values };
  const { response, error } = yield call(postTask, {
    url: Urls.api.createPaymentLink,
    body,
    token,
  });

  if (response) {
    segmentPaymentLinkCreatePaymentLink({
      amount: action.values.amount,
      currency: action.values.currency,
      productName: action.values.description,
      shareLink: action.values.sendEmail,
      clientEmail: Boolean(action.values.clientEmail),
      additionalClientInformation: Boolean(action.values.clientDocument || action.values.clientFirstName || action.values.clientLastName),
    })
    yield put(postCreatePaymentLinkSuccess(response));
  } else {
    segmentPaymentLinkCreatePaymentLink({
      amount: action.values.amount,
      currency: action.values.currency,
      productName: action.values.description,
      shareLink: action.values.sendEmail,
      clientEmail: Boolean(action.values.clientEmail),
      additionalClientInformation: Boolean(action.values.clientDocument || action.values.clientFirstName || action.values.clientLastName),
      error
    })
    yield put(postCreatePaymentLinkError({ ...error, sentValues: body, appState: state }));
  }
}

export function* createPaymentLinkSaga() {
  yield takeLatest(PaymentLinkActionType.CREATE_PAYMENT_LINK, createPaymentLink);
}

function* getExistingClients(action: any) {
  yield* checkToken();
  const token: string = yield select(AuthSelectors.getToken);
  const merchantId: string = yield select(state => state.dashboard.merchantMe?.id);

  let params = `?page=${action.page || 0}`;
  params += action.country ? `&country=${action.country}` : '';

  const { response, error } = yield call(getTask, {
    url: Urls.api.allClients.replace(':id', merchantId) + params,
    token,
  });

  if (response) {
    yield put(getExistingClientsSuccess(response));
  } else {
    yield put(getExistingClientsError(error));
  }
}

export function* getExistingClientsSaga() {
  yield takeLatest(PaymentLinkActionType.GET_EXISTING_CLIENTS, getExistingClients);
}

function* getCreatedPaymentLinks(action: any) {
  yield* checkToken();
  const token: string = yield select(AuthSelectors.getToken);

  const { response, error } = yield call(getTask, {
    url: Urls.api.allPaymentLinks + `?page=${action.page || 0}&page_size=${action.rowsPerPage || 10}`,
    token,
  });

  if (response) {
    yield put(getCreatedPaymentLinksSuccess(response));
  } else {
    yield put(getCreatedPaymentLinksError(error));
  }
}

export function* getCreatedPaymentLinksSaga() {
  yield takeLatest(PaymentLinkActionType.GET_CREATED_PAYMENT_LINKS, getCreatedPaymentLinks);
}
